import baseApi from "../base-api"
import * as yup from "yup"
export type Utility = {
  id?: string
  secondaryId?: number
  itemName?: string
  active?: boolean
  module?: ModuleEnumType
  additionalInformation?: string
  createdBy?: string
  createdAt?: string
  updatedAt?: string
  deletedAt?: string | null
}

export type CreateUtilityVariable = {
  itemName: string
  active: boolean
  module: ModuleEnumType
  additionalInformation: string
}

export type CreateUtilityResponse = {
  data: Utility
  meta: any
  msg: string
  success: boolean
}

export const ModuleEnum = {
  VESSEL: "vessel",
  SHIPPING_LINE: "shippingLine",
  SHIPPER: "shipper",
  PORT: "port",
  DESCRIPTION: "description",
  CONTAINER_TYPE: "containerType",
  SHIPMENT_STATUS: "shipmentStatus",
  CONSIGNEE: "consignee",
  INVOICE_PARTICULAR: "invoiceParticular"
} as const

export type ModuleEnumType = (typeof ModuleEnum)[keyof typeof ModuleEnum]

export type GetUtilitiesVariable = {
  page?: number
  limit?: number
  module?: ModuleEnumType
  itemName?: string
  active?: boolean
}
export type GetUtilitiesResponse = {
  data: Utility[]
  meta: any
  msg: string
  success: boolean
}

export type GetUtilityVariable = {
  id: string
}
export type GetUtilityResponse = {
  data: Utility
  meta: null
  msg: string
  success: boolean
}

export type UpdateUtilityVariable = {
  itemName?: string
  active: boolean
  module: ModuleEnumType
  additionalInformation: string
  id: string
}

export type UpdateUtilityResponse = {
  data: Utility
  meta: null
  msg: string
  success: boolean
}

export type DeleteUtilityVariable = {
  id: string
}

export type DeleteUtilityResponse = {
  data: boolean
  meta: null
  msg: string
  success: boolean
}

export const utilityInitialValues: CreateUtilityVariable = {
  itemName: "",
  active: true,
  module: "" as unknown as ModuleEnumType,
  additionalInformation: ""
}

export const createUtilitySchema = yup.object().shape({
  itemName: yup.string().optional(),
  active: yup.boolean().required("Active status is required"),
  module: yup.string().oneOf(Object.values(ModuleEnum), "Invalid module type").required("Module is required"),
  additionalInformation: yup.string().optional()
})
export const updateUtilitySchema = yup.object().shape({
  id: yup.string().required("ID is required"),
  itemName: yup.string().optional(),
  active: yup.boolean().required("Active status is required"),
  module: yup.string().oneOf(Object.values(ModuleEnum), "Invalid module type").required("Module is required"),
  additionalInformation: yup.string().optional()
})

// API slice for vendor module
export const utilitiesApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all vendors with pagination and search
    getUtilities: builder.query<GetUtilitiesResponse, GetUtilitiesVariable>({
      query: body => {
        console.log("body", body)
        const params = new URLSearchParams()
        Object.entries(body).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString())
          }
        })
        return {
          url: `utilities?${params.toString()}`,
          method: "GET"
        }
      },
      providesTags: ["Utility"]
    }),

    // Get single vendor by ID
    getUtility: builder.query<GetUtilityResponse, GetUtilityVariable>({
      query: body => {
        const params = new URLSearchParams()
        Object.entries(body).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString())
          }
        })
        return {
          url: `utilities/${params.toString()}`,
          method: "GET"
        }
      },
      providesTags: ["Utility"]
    }),

    // Create new vendor
    createUtility: builder.mutation<CreateUtilityResponse, CreateUtilityVariable>({
      query: utility => ({
        url: "utilities",
        method: "POST",
        body: utility
      }),
      invalidatesTags: ["Utility"]
    }),

    // Update existing vendor
    updateUtility: builder.mutation<UpdateUtilityResponse, UpdateUtilityVariable>({
      query: utility => ({
        url: `utilities/${utility.id}`,
        method: "PATCH",
        body: utility
      }),
      invalidatesTags: ["Utility"]
    }),

    // Delete vendor
    deleteUtility: builder.mutation<DeleteUtilityResponse, DeleteUtilityVariable>({
      query: utility => ({
        url: `utilities/${utility.id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Utility"]
    })
  })
})

export const {
  useGetUtilitiesQuery,
  useLazyGetUtilitiesQuery,
  useGetUtilityQuery,
  useCreateUtilityMutation,
  useUpdateUtilityMutation,
  useDeleteUtilityMutation
} = utilitiesApi
